import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { filter } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

/**
 * This service is designed to enhance the experience of users navigating an Angular application within an iframe (ex. Kinecta)
 * It provides functionalities to save the last navigated route in the session storage and recover it upon reinitialization,
 * ensuring a seamless user experience across page reloads or navigation events.
 */
@Injectable({
  providedIn: 'root'
})
export class IframeService {
  private logger = inject(SawDust);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private logContext(additional: Record<string, unknown> = {}) {
    return { component: 'IframeService', ...additional };
  }

  constructor(private router: Router) {}

  /**
   * A constant key used to store and retrieve the last navigated route from the session storage.
   * @private
   */
  private LAST_ROUTE_SKEY = 'lastIframeRoute';

  /**
   * Initializes the iframe service by recovering the last route (if any) from the session storage
   * and setting up a listener to save the current route to session storage on every navigation change.
   */
  public init() {
    // Route recovery logic is only applicable if the parent window is not a DX iframe (ex. Kinecta)
    // See comments on the parentIsDXIframe method for more details
    const LOG_MSG_SAME_ORIGIN = 'arcDX is iframed inside of itself (ex. esign) - route recovery disabled';
    this.parentIsDXIframe() ? this.logger.info(LOG_MSG_SAME_ORIGIN, this.logContext()) : this.initializeRouteRecovery();
    // we could skip check when origin matches localhost. what about when esign is iframed with url dev4-test-automation.myori.com?
  }

  /**
   * Initializes iframe route recovery and sets up a listener to save the current route on navigation changes.
   * It first attempts to navigate to the last known route from session storage, enhancing the user experience
   * by ensuring seamless navigation after reloads or when returning to the app. A subscription to navigation end
   * events updates the stored route, maintaining a consistent navigation state within iframes.
   */
  private initializeRouteRecovery(): void {
    const LOG_MSG_DX_IFRAMED = 'arcDX is iframed, but the parent is not arcDX (ex. lender/hb iframed) - initializing route recovery logic';
    this.logger.info(LOG_MSG_DX_IFRAMED, this.logContext());
    this.checkRecoverRoute();
    this.saveRouteOnChange();
  }

  /**
   * Recovers the last route from the session storage and navigates to it.
   * This method is utilized to ensure that the application can return to the last known route
   * after being reloaded or when navigated back to the application.
   * @private
   * @remarks
   * Checking !this.parentIsDXIframe() again is redundant, but it's a good practice to ensure the method is not called in the wrong context.
   */
  private checkRecoverRoute(): void {
    if (!this.isBrowser) return;
    const lastRoute = sessionStorage.getItem(this.LAST_ROUTE_SKEY);
    if (lastRoute && !this.parentIsDXIframe()) {
      this.logger.info('Recovered route from previous iframe session', this.logContext({ route: lastRoute }));
      this.router.navigate([lastRoute]);
    } else {
      this.logger.debug('No iframe route to recover', this.logContext({ lastRoute }));
    }
  }

  /**
   * Listens for navigation end events and saves the current route URL to the session storage.
   * This ensures the application remembers the last route visited by the user before the app was reloaded or navigated away from.
   * @private
   */
  private saveRouteOnChange(): void {
    if (!this.isBrowser) return;
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        sessionStorage.setItem(this.LAST_ROUTE_SKEY, event.urlAfterRedirects);
        this.logger.debug('Saved iframe route to session storage', this.logContext({ route: event.urlAfterRedirects }));
      });
  }

  /**
   * Checks if the parent window is a DX Iframe.
   * This is required because in the esign flow:
   * 1. The dx iframes itself
   * 2. The dx loads the esign embded
   * 3. After esign, the dx takes over once again, and issues a same origin window.parent command to the parent dx
   * 4. The parent dx uses this command to dismiss the esign slider and set the task to complete
   * NOTE: Funding iframe works differently - swbc issues the command to the parent dx
   * THEREFORE, **we do not want to implement route saving for the esign flow** - see #247826
   * @returns void
   * @remarks
   * TODO: THIS IS A TEMPORARY FIX - WE SHOULD REMOVE THIS ONCE WE USE THE PROPER REST / JS EMBED SOLUTION FOR ESIGN
   */
  public parentIsDXIframe(): boolean {
    if (!this.isBrowser) return false;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const hasDocusignQueryParam = ['sid', 'event', 'applicantId', 'eid'].some((param) => urlParams.has(param));

      // If they are the same, it means the current page is either not in an iframe or is in an iframe from the same origin.
      return hasDocusignQueryParam || window.location.origin === window.parent.location.origin;
    } catch (e) {
      // happens when the parent page is from a different origin due to cross-origin security policies
      this.logger.debug('Unable to evaluate parent iframe origin due to cross-origin restrictions', this.logContext());
      return false;
    }
  }
}
